import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Building2, Users } from 'lucide-react';
import PropertyCard from '../components/PropertyCard.tsx';
import { Property } from '../types.ts';
import { PARTNERS } from '../constants.ts';
import { getSupabase } from '../lib/supabase.ts';

interface HomeProps {
  featuredProperties: Property[];
}

const VIDEOS = [
  "https://videos.pexels.com/video-files/7578544/7578544-hd_1920_1080_30fps.mp4", 
  "https://videos.pexels.com/video-files/3770033/3770033-hd_1920_1080_25fps.mp4", 
  "https://videos.pexels.com/video-files/7578552/7578552-hd_1920_1080_30fps.mp4"
];

const Home: React.FC<HomeProps> = ({ featuredProperties }) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const errorCountRef = useRef(0);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleVideoEnd = () => {
    errorCountRef.current = 0;
    setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % VIDEOS.length);
  };

  const handleVideoError = () => {
    if (errorCountRef.current < VIDEOS.length) {
      errorCountRef.current += 1;
      setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % VIDEOS.length);
    } else {
      setVideoError(true);
    }
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const supabase = getSupabase();
    if (supabase) { 
        await supabase.from('leads').insert({
            id: Date.now().toString(),
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            property_id: 'general-inquiry'
        });
    }
    setTimeout(() => {
       setIsSubmitting(false); 
       setSuccess(true);
       setTimeout(() => { 
           setShowLeadForm(false); 
           setSuccess(false); 
           setFormData({ name: '', email: '', phone: '' }); 
       }, 3000);
    }, 1500);
  };

  return (
    <div className="flex flex-col w-full">
      <section className="relative h-screen min-h-[600px] w-full overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-slate-900/40 z-10"></div>
        
        {videoError ? (
           <img 
             src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=2071" 
             className="absolute inset-0 w-full h-full object-cover z-0 animate-fade-in"
             alt="Luxury Villa"
           />
        ) : (
          <video 
            key={currentVideoIndex}
            src={VIDEOS[currentVideoIndex]}
            autoPlay 
            muted 
            playsInline
            onEnded={handleVideoEnd}
            onError={handleVideoError}
            className="absolute inset-0 w-full h-full object-cover z-0"
            poster="https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=2071"
          />
        )}

        <div className="relative z-20 h-full flex flex-col justify-center items-center text-center px-6 max-w-5xl mx-auto">
          <span className="text-gold-400 tracking-[0.4em] uppercase text-xs md:text-sm font-bold mb-4 animate-fade-in-up">
            Guardian Housing Real Estate
          </span>
          <h1 className="text-4xl md:text-7xl font-serif font-bold text-white mb-8 leading-tight drop-shadow-2xl">
            From House to Home <br/> We Create Stories
          </h1>
          <p className="text-white/80 max-w-2xl mb-10 text-sm md:text-lg leading-relaxed font-light italic">
            "Redefining luxury living in the UAE through a foundation of trust, excellence, and a passion for finding your perfect space."
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/buy" className="px-10 py-4 bg-gold-500 hover:bg-gold-600 text-white text-xs font-bold uppercase tracking-widest transition-all rounded-sm shadow-xl">
              Discover Portfolio
            </Link>
            <button 
              onClick={() => setShowLeadForm(true)}
              className="px-10 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white text-xs font-bold uppercase tracking-widest transition-all rounded-sm"
            >
              Investor Relations
            </button>
          </div>
        </div>
      </section>

      {showLeadForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="bg-white rounded-none shadow-2xl max-w-md w-full p-10 animate-fade-in relative border-t-8 border-gold-500">
            <button onClick={() => setShowLeadForm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-black transition-colors">✕</button>
            <h3 className="text-2xl font-serif text-slate-900 mb-6 text-center">Inquire Privately</h3>
            {success ? (
                <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">✓</div>
                <h4 className="text-xl font-bold text-slate-900">Request Dispatched</h4>
                <p className="text-gray-500 mt-2 text-xs uppercase tracking-widest">Our Senior Advisor will contact you shortly.</p>
                </div>
            ) : (
                <form onSubmit={handleLeadSubmit} className="space-y-6">
                <input required type="text" placeholder="Full Name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full border p-3 rounded-sm text-xs outline-none focus:border-gold-500 bg-slate-50"/>
                <input required type="email" placeholder="Email Address" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full border p-3 rounded-sm text-xs outline-none focus:border-gold-500 bg-slate-50"/>
                <input required type="tel" placeholder="Phone (with country code)" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full border p-3 rounded-sm text-xs outline-none focus:border-gold-500 bg-slate-50"/>
                <button disabled={isSubmitting} type="submit" className="w-full bg-slate-900 text-white py-4 font-bold hover:bg-gold-500 transition-all uppercase tracking-widest text-[10px]">
                    {isSubmitting ? 'Authenticating...' : 'Submit Inquiry'}
                </button>
                </form>
            )}
            </div>
        </div>
      )}

      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="lg:w-1/2">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2053" 
                  alt="Luxury Interior" 
                  className="w-full h-[400px] md:h-[500px] object-cover rounded-sm shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-slate-900 p-8 shadow-xl hidden md:block border-t-4 border-gold-500">
                  <p className="text-gold-400 font-serif text-4xl font-bold mb-1">9+</p>
                  <p className="text-white text-[10px] uppercase font-bold tracking-[0.2em] leading-tight">Years of <br/>Elite Service</p>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2">
              <span className="text-gold-600 font-bold uppercase tracking-widest text-xs mb-3 block">Our Heritage</span>
              <h2 className="text-4xl md:text-5xl font-serif text-slate-900 mb-8 leading-tight">More Than Just a <br/>Property Transaction</h2>
              <p className="text-gray-500 leading-relaxed mb-6 font-light">
                At Guardian Housing, we understand that finding a home is a life-changing milestone. Our team of specialized consultants is dedicated to providing the expertise and transparency you need for informed decision-making in the UAE market.
              </p>
              <p className="text-gray-500 leading-relaxed mb-10 font-light">
                With an intimate understanding of high-value communities, we offer a bespoke approach tailored to your unique portfolio goals. From first-time homeowners to seasoned institutional investors, we ensure excellence in every detail.
              </p>
              <Link to="/buy" className="text-slate-900 font-bold border-b-2 border-gold-500 hover:text-gold-600 transition-all inline-flex items-center gap-2 uppercase text-xs tracking-widest pb-1">
                Explore the Portfolio <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-4">
            <div>
              <span className="text-gold-600 font-bold uppercase tracking-widest text-xs mb-3 block">Exclusive Selects</span>
              <h2 className="text-4xl font-serif text-slate-900">Featured UAE Properties</h2>
            </div>
            <Link to="/buy" className="text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-gold-500 transition-all flex items-center gap-2">
              View All Listings <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProperties.slice(0, 4).map((prop) => (
              <PropertyCard key={prop.id} property={prop} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white border-t border-gray-100">
        <div className="container mx-auto px-6">
          <p className="text-center text-gray-400 text-[10px] font-bold uppercase tracking-[0.4em] mb-12">Authorized Developer Partnerships</p>
          <div className="flex flex-wrap justify-center items-center gap-10 md:gap-20 opacity-40 grayscale hover:grayscale-0 transition-all duration-700">
             {PARTNERS.map((partner) => (
               <span key={partner} className="text-xl md:text-3xl font-serif font-bold text-slate-900 tracking-widest">
                 {partner}
               </span>
             ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;